import { useState, useEffect } from 'react';
import { FaVolumeUp, FaSpinner, FaImage } from 'react-icons/fa';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../../utilities/extensions/textToSpeech';
import { db } from '../../../firebase';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';

const ChangeGendersActivity = ({ activityId, initialContent, onContentChange, chapterId, grade, selectedChapter }) => {
  const [content, setContent] = useState({
    pairs: [
      {
        maleLanguageWord: '',
        maleEnglishWord: '',
        femaleLanguageWord: '',
        femaleEnglishWord: '',
        maleImage: null,
        femaleImage: null,
        maleConfusingLetters: '',
        femaleConfusingLetters: '',
        maleLanguageAudio: null,
        maleEnglishAudio: null,
        femaleLanguageAudio: null,
        femaleEnglishAudio: null
      }
    ]
  });

  const [uploadingFiles, setUploadingFiles] = useState({});
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [generatingImages, setGeneratingImages] = useState(false);

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent);
        if (parsed && Array.isArray(parsed.pairs)) {
          setContent(parsed);
        }
      } catch {
        // keep default
      }
    }
  }, [initialContent]);

  useEffect(() => {
    onContentChange(content);
  }, [content, onContentChange]);

  const buildStoragePath = ({ pairIndex, variant, fileName }) => {
    const language = selectedChapter?.subject?.toLowerCase() || 'language';
    const gradeStr = `grade${grade}`;

    return generateActivityStoragePath({
      language,
      grade: gradeStr,
      chapterId,
      activityName: 'change_genders',
      fileName,
      itemId: `pair${pairIndex + 1}-${variant}`
    });
  };

  const playAudio = async (audioPath) => {
    if (!audioPath) return;

    try {
      const storage = getStorage();
      const audioRef = ref(storage, audioPath);
      const url = await getDownloadURL(audioRef);
      const audio = new Audio(url);
      audio.play().catch(() => {});
    } catch (error) {
      alert(`Failed to play audio. Error: ${error.message}`);
    }
  };

  const generateAllAudio = async () => {
    const pairs = content.pairs || [];
    if (!pairs.length) {
      alert('No pairs available to generate audio');
      return;
    }

    setGeneratingAudio(true);

    try {
      const storage = getStorage();
      const updatedPairs = [...pairs];

      const languageTexts = [];
      const englishTexts = [];

      pairs.forEach((p) => {
        if (p.maleLanguageWord) languageTexts.push(p.maleLanguageWord);
        if (p.femaleLanguageWord) languageTexts.push(p.femaleLanguageWord);
        if (p.maleEnglishWord) englishTexts.push(p.maleEnglishWord);
        if (p.femaleEnglishWord) englishTexts.push(p.femaleEnglishWord);
      });

      if (!languageTexts.length && !englishTexts.length) {
        alert('No words found to generate audio. Please add words first.');
        setGeneratingAudio(false);
        return;
      }

      let audioGenerated = false;

      if (languageTexts.length) {
        const languageRequests = languageTexts.map(text => ({
          text,
          languageCode: LanguageCodes.KANNADA,
          ssmlGender: VoiceGenders.FEMALE
        }));

        const languageAudioUrls = await generateBatchSpeechAudio(languageRequests, db, storage);
        let idx = 0;
        for (let i = 0; i < updatedPairs.length; i += 1) {
          const p = updatedPairs[i];
          if (p.maleLanguageWord && idx < languageAudioUrls.length) {
            const url = languageAudioUrls[idx];
            if (url && !url.startsWith('[TTS Error:')) {
              p.maleLanguageAudio = url;
              audioGenerated = true;
            }
            idx += 1;
          }
          if (p.femaleLanguageWord && idx < languageAudioUrls.length) {
            const url = languageAudioUrls[idx];
            if (url && !url.startsWith('[TTS Error:')) {
              p.femaleLanguageAudio = url;
              audioGenerated = true;
            }
            idx += 1;
          }
        }
      }

      if (englishTexts.length) {
        const englishRequests = englishTexts.map(text => ({
          text,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE
        }));

        const englishAudioUrls = await generateBatchSpeechAudio(englishRequests, db, storage);
        let idx = 0;
        for (let i = 0; i < updatedPairs.length; i += 1) {
          const p = updatedPairs[i];
          if (p.maleEnglishWord && idx < englishAudioUrls.length) {
            const url = englishAudioUrls[idx];
            if (url && !url.startsWith('[TTS Error:')) {
              p.maleEnglishAudio = url;
              audioGenerated = true;
            }
            idx += 1;
          }
          if (p.femaleEnglishWord && idx < englishAudioUrls.length) {
            const url = englishAudioUrls[idx];
            if (url && !url.startsWith('[TTS Error:')) {
              p.femaleEnglishAudio = url;
              audioGenerated = true;
            }
            idx += 1;
          }
        }
      }

      setContent(prev => ({ ...prev, pairs: updatedPairs }));

      if (audioGenerated) {
        alert('Audio generated successfully for all words!');
      } else {
        alert('Failed to generate audio. Please check the words and try again.');
      }
    } catch (error) {
      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  const generateMissingImages = async () => {
    const pairs = content.pairs || [];
    if (!pairs.length) {
      alert('No pairs available to generate images');
      return;
    }

    const hasMissingImages = pairs.some(p => !p.maleImage || !p.femaleImage);
    if (!hasMissingImages) {
      alert('All pairs already have images');
      return;
    }

    setGeneratingImages(true);

    try {
      const functions = getFunctions();
      const generateImagesFn = httpsCallable(functions, 'generateWordMeaningImages');
      
      await generateImagesFn({
        activityId,
        baseContent: JSON.stringify(content),
        chapterId,
        language: (selectedChapter?.subject || 'language').toLowerCase(),
        grade,
        activityType: 'CHANGE_GENDERS',
      });

      alert('Image generation started. Please refresh the page in a few moments to see the images.');
    } catch (error) {
      console.error('Failed to generate images:', error);
      alert('Failed to generate images: ' + error.message);
    } finally {
      setGeneratingImages(false);
    }
  };

  const updatePair = (index, field, value) => {
    const updated = [...(content.pairs || [])];
    updated[index] = { ...updated[index], [field]: value };
    setContent(prev => ({ ...prev, pairs: updated }));
  };

  const addPair = () => {
    setContent(prev => ({
      ...prev,
      pairs: [
        ...(prev.pairs || []),
        {
          maleLanguageWord: '',
          maleEnglishWord: '',
          femaleLanguageWord: '',
          femaleEnglishWord: '',
          maleImage: null,
          femaleImage: null,
          maleConfusingLetters: '',
          femaleConfusingLetters: '',
          maleLanguageAudio: null,
          maleEnglishAudio: null,
          femaleLanguageAudio: null,
          femaleEnglishAudio: null
        }
      ]
    }));
  };

  const removePair = async (index) => {
    const pairs = content.pairs || [];
    if (pairs.length <= 1) return;

    const pair = pairs[index];
    const storage = getStorage();

    const imageFields = ['maleImage', 'femaleImage'];
    for (const field of imageFields) {
      const path = pair[field];
      if (path) {
        try {
          const imgRef = ref(storage, path);
          await deleteObject(imgRef);
        } catch {
          // ignore
        }
      }
    }

    const updated = pairs.filter((_, i) => i !== index);
    setContent(prev => ({ ...prev, pairs: updated }));
  };

  const handleFileUpload = async (index, variant, file) => {
    if (!file || !chapterId || !grade) return;

    const uploadKey = `${index}-${variant}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const pairs = content.pairs || [];
      const field = variant === 'male' ? 'maleImage' : 'femaleImage';
      const existingPath = pairs[index]?.[field];

      if (existingPath) {
        try {
          const existingRef = ref(storage, existingPath);
          await deleteObject(existingRef);
        } catch {
          // ignore delete
        }
      }

      const storagePath = buildStoragePath({ pairIndex: index, variant, fileName: file.name });
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);

      const updated = [...pairs];
      updated[index] = { ...updated[index], [field]: storagePath };
      setContent(prev => ({ ...prev, pairs: updated }));
    } catch {
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const previewImage = async (path) => {
    if (!path) return;
    try {
      const storage = getStorage();
      const imgRef = ref(storage, path);
      const url = await getDownloadURL(imgRef);
      window.open(url, '_blank');
    } catch (error) {
      alert(`Failed to preview image. Error: ${error.message}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Change Genders</h3>
            <p className="text-sm text-gray-600 mt-1">Add male/female forms with audio, images and confusing letters.</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-sm text-sm disabled:bg-purple-300 disabled:cursor-not-allowed"
              onClick={generateMissingImages}
              disabled={generatingImages || !(content.pairs && content.pairs.length)}
            >
              {generatingImages ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Generating Images...
                </>
              ) : (
                <>
                  <FaImage className="w-4 h-4 mr-2" />
                  Generate Missing Images
                </>
              )}
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-sm text-sm disabled:bg-emerald-300 disabled:cursor-not-allowed"
              onClick={generateAllAudio}
              disabled={generatingAudio || !(content.pairs && content.pairs.length)}
            >
              {generatingAudio ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FaVolumeUp className="w-4 h-4 mr-2" />
                  Generate Audio
                </>
              )}
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm text-sm"
              onClick={addPair}
            >
              + Add Pair
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {(content.pairs || []).map((row, idx) => (
            <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {idx + 1}
                </div>
                {(content.pairs || []).length > 1 && (
                  <button
                    type="button"
                    className="ml-auto p-2 text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                    onClick={() => removePair(idx)}
                    title="Remove pair"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Male side */}
                <div className="bg-white rounded-lg p-4 border border-gray-200 space-y-3">
                  <h4 className="font-semibold text-gray-800 text-sm">Male Gender</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Language word (Male) *"
                      value={row.maleLanguageWord || ''}
                      onChange={e => updatePair(idx, 'maleLanguageWord', e.target.value)}
                    />
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="English word (Male)"
                      value={row.maleEnglishWord || ''}
                      onChange={e => updatePair(idx, 'maleEnglishWord', e.target.value)}
                    />
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Confusing letters (Male)"
                      value={row.maleConfusingLetters || ''}
                      onChange={e => updatePair(idx, 'maleConfusingLetters', e.target.value)}
                    />
                  </div>

                  <div className="flex items-center gap-2 text-xs text-gray-600 flex-wrap">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id={`change-genders-male-img-${idx}`}
                      onChange={e => handleFileUpload(idx, 'male', e.target.files[0])}
                      disabled={uploadingFiles[`${idx}-male`]}
                    />
                    <label
                      htmlFor={`change-genders-male-img-${idx}`}
                      className="cursor-pointer px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs hover:bg-purple-100 transition-colors"
                    >
                      🖼️ Image
                    </label>
                    {uploadingFiles[`${idx}-male`] && (
                      <span className="text-purple-600 text-[10px]">Uploading...</span>
                    )}
                    {row.maleImage && !uploadingFiles[`${idx}-male`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                          onClick={() => previewImage(row.maleImage)}
                          title="Preview image"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => updatePair(idx, 'maleImage', null)}
                          title="Remove image"
                        >
                          ✕
                        </button>
                      </>
                    )}
                    {row.maleLanguageAudio && (
                      <button
                        type="button"
                        className="flex items-center gap-1 px-2 py-1 text-green-600 hover:bg-green-100 rounded"
                        onClick={() => playAudio(row.maleLanguageAudio)}
                        title="Play language audio (Male)"
                      >
                        <FaVolumeUp className="w-3 h-3" />
                        <span className="text-[10px] font-medium">Lang</span>
                      </button>
                    )}
                    {row.maleEnglishAudio && (
                      <button
                        type="button"
                        className="flex items-center gap-1 px-2 py-1 text-green-600 hover:bg-green-100 rounded"
                        onClick={() => playAudio(row.maleEnglishAudio)}
                        title="Play English audio (Male)"
                      >
                        <FaVolumeUp className="w-3 h-3" />
                        <span className="text-[10px] font-medium">Eng</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Female side */}
                <div className="bg-white rounded-lg p-4 border border-gray-200 space-y-3">
                  <h4 className="font-semibold text-gray-800 text-sm">Female Gender</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Language word (Female) *"
                      value={row.femaleLanguageWord || ''}
                      onChange={e => updatePair(idx, 'femaleLanguageWord', e.target.value)}
                    />
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="English word (Female)"
                      value={row.femaleEnglishWord || ''}
                      onChange={e => updatePair(idx, 'femaleEnglishWord', e.target.value)}
                    />
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Confusing letters (Female)"
                      value={row.femaleConfusingLetters || ''}
                      onChange={e => updatePair(idx, 'femaleConfusingLetters', e.target.value)}
                    />
                  </div>

                  <div className="flex items-center gap-2 text-xs text-gray-600 flex-wrap">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id={`change-genders-female-img-${idx}`}
                      onChange={e => handleFileUpload(idx, 'female', e.target.files[0])}
                      disabled={uploadingFiles[`${idx}-female`]}
                    />
                    <label
                      htmlFor={`change-genders-female-img-${idx}`}
                      className="cursor-pointer px-2 py-1 bg-pink-50 text-pink-700 rounded text-xs hover:bg-pink-100 transition-colors"
                    >
                      🖼️ Image
                    </label>
                    {uploadingFiles[`${idx}-female`] && (
                      <span className="text-pink-600 text-[10px]">Uploading...</span>
                    )}
                    {row.femaleImage && !uploadingFiles[`${idx}-female`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                          onClick={() => previewImage(row.femaleImage)}
                          title="Preview image"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => updatePair(idx, 'femaleImage', null)}
                          title="Remove image"
                        >
                          ✕
                        </button>
                      </>
                    )}
                    {row.femaleLanguageAudio && (
                      <button
                        type="button"
                        className="flex items-center gap-1 px-2 py-1 text-green-600 hover:bg-green-100 rounded"
                        onClick={() => playAudio(row.femaleLanguageAudio)}
                        title="Play language audio (Female)"
                      >
                        <FaVolumeUp className="w-3 h-3" />
                        <span className="text-[10px] font-medium">Lang</span>
                      </button>
                    )}
                    {row.femaleEnglishAudio && (
                      <button
                        type="button"
                        className="flex items-center gap-1 px-2 py-1 text-green-600 hover:bg-green-100 rounded"
                        onClick={() => playAudio(row.femaleEnglishAudio)}
                        title="Play English audio (Female)"
                      >
                        <FaVolumeUp className="w-3 h-3" />
                        <span className="text-[10px] font-medium">Eng</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const validateChangeGendersContent = (content) => {
  const errors = [];

  if (!content.pairs || content.pairs.length === 0) {
    errors.push('At least one pair is required.');
  }

  return errors;
};

export default ChangeGendersActivity;
